import React, { useMemo, useState } from 'react';
import { icons as lucideIcons } from 'lucide-react';

// ========================================
// StageIcon - renders a stored icon value
// ========================================
// Stored icon values are lucide PascalCase names (e.g. "Phone").
// Legacy emoji values (e.g. "📋") fall back to a text span so existing
// stages and backend default stages keep rendering without migration.
export const StageIcon = ({ icon, className = 'w-5 h-5' }) => {
  const Cmp = lucideIcons[icon];
  if (Cmp) return <Cmp className={className} />;
  return <span className={className}>{icon}</span>;
};

// ========================================
// Suggested icons - curated subset relevant to lead/pipeline stages
// ========================================
const SUGGESTED_ICONS = [
  'ClipboardList', 'ClipboardCheck', 'ListChecks', 'ListPlus',
  'UserPlus', 'UserCheck', 'UserX', 'Users',
  'Phone', 'PhoneCall', 'PhoneIncoming', 'PhoneOutgoing', 'PhoneMissed',
  'Mail', 'MessageSquare', 'MessageCircle', 'Send',
  'Calendar', 'CalendarClock', 'Clock', 'AlarmClock',
  'FileText', 'FileCheck', 'Signature', 'FilePlus',
  'Target', 'Crosshair', 'Flag', 'TrendingUp',
  'CircleCheck', 'CircleCheckBig', 'CircleDot',
  'CircleX', 'Ban', 'OctagonX',
  'Flame', 'Snowflake', 'Zap', 'Sparkles',
  'Briefcase', 'Building2', 'Globe', 'MapPin',
  'Handshake', 'PartyPopper', 'Trophy', 'Award',
  'Star', 'Heart', 'ThumbsUp', 'Smile',
  'DollarSign', 'BadgeDollarSign', 'TrendingDown', 'Activity',
  'Bell', 'Bookmark', 'Tag', 'Flag',
  'RefreshCw', 'Repeat', 'RotateCw',
];

const IconPicker = ({ value, onChange, color }) => {
  const [query, setQuery] = useState('');

  // Full searchable list of every lucide icon name.
  const allNames = useMemo(() => Object.keys(lucideIcons), []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SUGGESTED_ICONS.filter((name) => lucideIcons[name]);
    return allNames
      .filter((name) => name.toLowerCase().includes(q))
      .slice(0, 144); // cap grid size for render perf
  }, [query, allNames]);

  const PreviewCmp = lucideIcons[value];
  const showingSuggestions = !query.trim();

  return (
    <div className="border border-gray-200 rounded-lg p-3">
      {/* Current selection preview */}
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: color ? `${color}1A` : '#EEF2FF', color: color || '#4F46E5' }}
        >
          {PreviewCmp ? (
            <PreviewCmp className="w-5 h-5" />
          ) : (
            <span className="text-xl leading-none">{value}</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900 truncate">
            {PreviewCmp ? value : 'Emoji (legacy)'}
          </p>
          <p className="text-xs text-gray-500 truncate">
            {PreviewCmp ? 'Lucide icon' : 'Pick an icon to replace'}
          </p>
        </div>
      </div>

      {/* Search */}
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search icons (e.g. phone, target, check)..."
        className="w-full px-3 py-2 mb-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
      />

      {/* Grid */}
      <div className="max-h-56 overflow-y-auto -mx-1 px-1">
        {showingSuggestions && (
          <p className="text-xs font-medium text-gray-500 mb-2">Suggested</p>
        )}
        <div className="grid grid-cols-6 sm:grid-cols-8 gap-1.5 sm:gap-2">
          {filtered.map((name) => {
            const Cmp = lucideIcons[name];
            if (!Cmp) return null;
            const selected = value === name;
            return (
              <button
                key={name}
                type="button"
                title={name}
                onClick={() => onChange(name)}
                className={`aspect-square flex items-center justify-center rounded-lg border transition-colors ${
                  selected
                    ? 'border-primary-500 bg-primary-50 text-primary-600'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <Cmp className="w-5 h-5 sm:w-4 sm:h-4" />
              </button>
            );
          })}
        </div>
        {filtered.length === 0 && (
          <p className="text-center text-sm text-gray-400 py-4">No icons match "{query}"</p>
        )}
      </div>
    </div>
  );
};

export default IconPicker;